import moment from "moment";

const DATE_FORMAT = "YYYY-MM-DD";
const DATE_FORMAT_WITH_TIME = "YYYY-MM-DDTHH:mm:ss";

export const UtcHelper = {
  utcOffset: moment().utcOffset() / 60,

  isFloatUtcOffset(): boolean {
    return !Number.isInteger(this.utcOffset);
  },

  isWesternUtcOffset(): boolean {
    return this.utcOffset < 0;
  },

  isEasternUtcOffset(): boolean {
    return this.utcOffset > 0;
  },

  getMonthInitialDate(): string {
    const date = moment().startOf("month");

    if (this.isEasternUtcOffset()) {
      date.subtract(1, "day");
    }

    return this.formatInitialDate({ date });
  },

  getMonthEndDate(): string {
    const date = moment().endOf("month");

    if (this.isWesternUtcOffset()) {
      date.add(1, "day");
    }

    return this.formatEndDate({ date });
  },

  getEasternUtcFilterInitialDate({ date }): string {
    const adjustedDate = moment(date).subtract(1, "day");

    return this.formatInitialDate({ date: adjustedDate });
  },

  getWesternUtcFilterEndDate({ date }): string {
    const adjustedDate = moment(date).add(1, "day");

    return this.formatEndDate({ date: adjustedDate });
  },

  getUtcOffsetString({ endOfPeriod }): string {
    // this floors float UTC offsets which are handled in getUtcOffsetMinuteString method
    const utcOffsetToFloor = Math.floor(this.utcOffset);

    let utcOffsetValue = Math.abs(utcOffsetToFloor);

    if (this.isWesternUtcOffset()) {
      if (endOfPeriod) utcOffsetValue -= 1;
    }

    if (this.isEasternUtcOffset()) {
      if (endOfPeriod && !this.isFloatUtcOffset()) utcOffsetValue += 1;
      utcOffsetValue = 24 - utcOffsetValue;
    }

    return utcOffsetValue.toString().padStart(2, "0");
  },

  getUtcOffsetMinuteString({ endOfPeriod }): string {
    // float UTC offsets
    if (this.isFloatUtcOffset()) {
      // .25 decimal Offset
      if (this.utcOffset % 0.5 !== 0) {
        if (endOfPeriod) {
          return "44";
        }
        return "45";
      }

      // .5 decimal Offset
      if (endOfPeriod) {
        return "29";
      }
      return "30";
    }

    // integer UTC offsets
    if (endOfPeriod) {
      return "59";
    }

    return "00";
  },

  getTodayWithUtcOffset(): string {
    const today = moment().format(DATE_FORMAT);
    return this.formatInitialDateWithUtcOffset({ date: today });
  },

  getToday(): string {
    const today = moment().format(DATE_FORMAT);
    return this.formatInitialDate({ date: today });
  },

  getNow(): string {
    return moment().format(DATE_FORMAT_WITH_TIME);
  },

  setEasternReportDate(reportDate: string): string {
    reportDate = moment(new Date(reportDate))
      .add(1, "day")
      .format(DATE_FORMAT);
    return reportDate;
  },

  formatInitialDateWithUtcOffset({ date }): string {
    const dateFormatted = moment(date).format(DATE_FORMAT);
    const utcOffsetString = this.getUtcOffsetString({ endOfPeriod: false });
    const utcOffsetMinuteString = this.getUtcOffsetMinuteString({
      endOfPeriod: false
    });

    return `${dateFormatted}T${utcOffsetString}:${utcOffsetMinuteString}:00`;
  },

  formatInitialDate({ date }): string {
    const dateFormatted = moment(date)
      .startOf("day")
      .format(DATE_FORMAT_WITH_TIME);
    return `${dateFormatted}`;
  },

  formatEndDate({ date }) {
    const dateFormatted = moment(date)
      .startOf("day")
      .format(DATE_FORMAT_WITH_TIME);

    return `${dateFormatted}`;
  },

  formatEndDateWithUtcOffset({ date }) {
    const dateFormatted = moment(date).format(DATE_FORMAT);
    const utcOffsetString = this.getUtcOffsetString({ endOfPeriod: true });
    const utcOffsetMinuteString = this.getUtcOffsetMinuteString({
      endOfPeriod: true
    });

    return `${dateFormatted}T${utcOffsetString}:${utcOffsetMinuteString}:59`;
  },

  getNextDayWithUtcOffset({ date }) {
    const nextDay = moment(date)
      .add(1, "day")
      .format(DATE_FORMAT);
    const utcOffsetString = this.getUtcOffsetString({ endOfPeriod: true });
    const utcOffsetMinuteString = this.getUtcOffsetMinuteString({
      endOfPeriod: true
    });

    return `${nextDay}T${utcOffsetString}:${utcOffsetMinuteString}:59`;
  },

  getNextDay({ date }) {
    const nextDay = moment(date)
      .add(1, "day")
      .endOf("day")
      .format(DATE_FORMAT_WITH_TIME);

    return `${nextDay}`;
  },

  getEndOfDay({ date }) {
    const endOfDay = moment(date)
      .endOf("day")
      .format(DATE_FORMAT_WITH_TIME);

    return endOfDay;
  }
};
